import { forwardRef, useImperativeHandle, useMemo } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { PdfPageCanvas } from "./components/PdfPageCanvas";
import { PdfPagePlaceholder } from "./components/PdfPagePlaceholder";
import { usePageVisibilityObserver, ScrollAnchor, MatchRect } from "./hooks/usePageVisibilityObserver";
import { useVisiblePages } from "./hooks/useVisiblePages";
import type { PdfAnnotation } from "./annotations/types";
import type { PdfSearchMatchRect } from "./hooks/usePdfSearchWithRects";

export interface PdfInfiniteScrollerRef {
  scrollToPage: (page: number) => void;
  scrollToMatchRect: (match: MatchRect, zoom: number) => void;
  captureScrollAnchor: () => ScrollAnchor | null;
  restoreScrollAnchor: (anchor: ScrollAnchor) => void;
}

interface PdfInfiniteScrollerProps {
  pdf: PDFDocumentProxy;
  numPages: number;
  zoom: number;
  rotation: number;
  darkMode: boolean;
  pageSize: { width: number; height: number } | null;
  onPageChange: (page: number) => void;
  annotations?: PdfAnnotation[];
  onAnnotationClick?: (annotation: PdfAnnotation, anchorRect: DOMRect, pageNumber: number) => void;
  searchMatches?: PdfSearchMatchRect[];
  activeSearchMatch?: PdfSearchMatchRect | null;
}

export const PdfInfiniteScroller = forwardRef<PdfInfiniteScrollerRef, PdfInfiniteScrollerProps>(
  function PdfInfiniteScroller(
    { pdf, numPages, zoom, rotation, darkMode, pageSize, onPageChange, annotations = [], onAnnotationClick, searchMatches = [], activeSearchMatch = null },
    ref
  ) {
    const {
      containerRef,
      pageRefs,
      handleScroll,
      scrollToPage,
      scrollToMatchRect,
      captureScrollAnchor,
      restoreScrollAnchor,
    } = usePageVisibilityObserver(numPages, onPageChange);

    const { visiblePages } = useVisiblePages(containerRef, pageRefs, numPages);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      scrollToPage,
      scrollToMatchRect,
      captureScrollAnchor,
      restoreScrollAnchor,
    }), [scrollToPage, scrollToMatchRect, captureScrollAnchor, restoreScrollAnchor]);

    // Calculate placeholder dimensions based on zoom and rotation
    const placeholderDimensions = useMemo(() => {
      if (!pageSize) return { width: 595, height: 842 }; // A4 default
      
      const isRotated = rotation === 90 || rotation === 270;
      const width = isRotated ? pageSize.height * zoom : pageSize.width * zoom;
      const height = isRotated ? pageSize.width * zoom : pageSize.height * zoom;
      
      return { width, height };
    }, [pageSize, zoom, rotation]);

    return (
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className={`pdf-scroll-container ${darkMode ? "bg-muted" : "bg-muted/30"}`}
      >
        {Array.from({ length: numPages }, (_, i) => {
          const pageNumber = i + 1;
          const shouldRender = visiblePages.has(pageNumber);
          const pageAnnotations = annotations.filter((a) => a.pageNumber === pageNumber);
          const pageSearchMatches = searchMatches.filter((m) => m.pageNumber === pageNumber);

          return (
            <div
              key={pageNumber}
              data-page-number={pageNumber}
              ref={(el) => (pageRefs.current[i] = el)}
              className="pdf-page-container relative"
            >
              {shouldRender ? (
                <PdfPageCanvas
                  pdf={pdf}
                  pageNumber={pageNumber}
                  zoom={zoom}
                  rotation={rotation}
                  darkMode={darkMode}
                  pageRef={pageRefs.current[i]}
                  annotations={pageAnnotations}
                  onAnnotationClick={onAnnotationClick}
                  searchMatches={pageSearchMatches}
                  activeSearchMatch={activeSearchMatch}
                />
              ) : (
                <PdfPagePlaceholder
                  width={placeholderDimensions.width}
                  height={placeholderDimensions.height}
                  pageNumber={pageNumber}
                  darkMode={darkMode}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
);
